import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { SeoHead } from '../components/SeoHead';
import { Button } from '../components/ui/button';
import {
  Calendar,
  MapPin,
  ChevronLeft,
  Clock,
  Loader2,
  Play,
  Search,
  X,
  Ticket,
  Check,
  Lock,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Hero background images - Basketball courts and action
const HERO_IMAGE = 'https://images.unsplash.com/photo-1677617586882-2b494292ebbe?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920';

// Game card backgrounds
const GAME_BACKGROUNDS = {
  live: 'https://images.unsplash.com/photo-1771909713196-a23e15bdbbf3?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200',
  replay: 'https://images.unsplash.com/photo-1677617586879-ad2ae3b61099?crop=entropy&cs=srgb&fm=jpg&q=85&w=800',
  upcoming: 'https://images.unsplash.com/photo-1587296104393-8db6cda4418d?crop=entropy&cs=srgb&fm=jpg&q=85&w=800',
};

const EventDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [event, setEvent] = useState(null);
  const [eventGames, setEventGames] = useState([]);

  // Event logo - from event data, or EK classic logo for that event, or Rooam logo fallback
  const eventLogoUrl = event?.logo_url || (event?.id === 'efb99264-287c-4957-857e-59fac86cda01' ? '/ek-cali-classic-logo.png' : '/rooam-logo.png');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('games');
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  
  // Passes
  const [passTiers, setPassTiers] = useState([]);
  const [userAccess, setUserAccess] = useState(null);
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [passDiscountInput, setPassDiscountInput] = useState({});
  const [passDiscountValid, setPassDiscountValid] = useState({});
  const [passDiscountInfo, setPassDiscountInfo] = useState({});
  const [validatingPassCode, setValidatingPassCode] = useState(null);
  
  // Filters
  const [dayFilter, setDayFilter] = useState('');
  const [courtFilter, setCourtFilter] = useState('');
  const [divisionFilter, setDivisionFilter] = useState('');

  useEffect(() => {
    fetchEvent();
  }, [id]);

  useEffect(() => {
    if (event) {
      fetchEventGames();
      fetchPassTiers();
    }
  }, [event]);

  useEffect(() => {
    if (event && isAuthenticated()) {
      checkAccess();
    }
  }, [event, user]);

  const fetchPassTiers = async () => {
    try {
      const res = await fetch(`${API_URL}/api/events/${id}/passes`);
      if (res.ok) {
        const data = await res.json();
        setPassTiers(data.tiers || []);
      }
    } catch (err) {
      console.error('Error fetching pass tiers:', err);
    }
  };

  const checkAccess = async () => {
    try {
      const token = sessionStorage.getItem('rooam_token') || localStorage.getItem('rooam_token');
      if (!token) return;
      const res = await fetch(`${API_URL}/api/events/${id}/access`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setUserAccess(data);
      }
    } catch (err) {
      console.error('Error checking access:', err);
    }
  };

  const validatePassDiscount = async (tierId) => {
    const code = (passDiscountInput[tierId] || '').trim();
    if (!code) return;
    setValidatingPassCode(tierId);
    try {
      const res = await fetch(`${API_URL}/api/discount-codes/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code }),
      });
      const data = await res.json();
      setPassDiscountValid(prev => ({ ...prev, [tierId]: data.valid }));
      setPassDiscountInfo(prev => ({ ...prev, [tierId]: data.valid ? data : null }));
      if (data.valid) {
        toast.success(`${data.discount_percent}% discount — applied at checkout`);
      } else {
        toast.error(data.message || 'Invalid discount code');
      }
    } catch {
      toast.error('Failed to validate code');
      setPassDiscountValid(prev => ({ ...prev, [tierId]: false }));
    }
    setValidatingPassCode(null);
  };

  const handleBuyPass = async (tierId) => {
    if (!isAuthenticated()) {
      toast.error('Please create an account or log in to purchase a pass.');
      navigate('/register');
      return;
    }
    setCheckoutLoading(tierId);
    try {
      const token = sessionStorage.getItem('rooam_token') || localStorage.getItem('rooam_token');
      const bodyData = {};
      if (passDiscountValid[tierId] && passDiscountInput[tierId]) {
        bodyData.discount_code = passDiscountInput[tierId];
      }
      const res = await fetch(`${API_URL}/api/events/${id}/passes/${tierId}/checkout`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(bodyData),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Checkout failed');
      }
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (err) {
      toast.error(err.message || 'Failed to start checkout');
    } finally {
      setCheckoutLoading(null);
    }
  };

  const fetchEvent = async (isRetry = false) => {
    if (!isRetry) setLoading(true);
    setError(null);
    
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15000);
      
      const eventRes = await fetch(`${API_URL}/api/events/${id}`, { signal: controller.signal });
      clearTimeout(timeoutId);
      
      if (eventRes.ok) {
        const data = await eventRes.json();
        setEvent({ ...data, type: 'event' });
        setLoading(false);
        return;
      }

      const tournamentRes = await fetch(`${API_URL}/api/tournaments/${id}`);
      if (tournamentRes.ok) {
        const data = await tournamentRes.json();
        setEvent({ ...data, type: 'tournament' });
        setLoading(false);
        return;
      }

      setEvent(null);
    } catch (err) {
      console.error('Error fetching event:', err);
      // Auto-retry once on network error
      if (!isRetry && (err.name === 'AbortError' || err.name === 'TypeError')) {
        setTimeout(() => fetchEvent(true), 2000);
        return;
      }
      setError('Connection error. Please try again.');
      toast.error('Failed to load event');
    }
    setLoading(false);
  };

  const fetchEventGames = async () => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15000);
      
      const gamesRes = await fetch(`${API_URL}/api/engine/games?event_id=${id}&limit=100`, { signal: controller.signal });
      clearTimeout(timeoutId);
      
      if (gamesRes.ok) {
        const games = await gamesRes.json();
        games.sort((a, b) => {
          const dateA = a.scheduled_time ? new Date(a.scheduled_time) : (a.scheduled_at ? new Date(a.scheduled_at) : new Date(0));
          const dateB = b.scheduled_time ? new Date(b.scheduled_time) : (b.scheduled_at ? new Date(b.scheduled_at) : new Date(0));
          return dateA - dateB;
        });
        setEventGames(games);
      }
    } catch (error) {
      console.error('Error fetching games:', error);
    }
  };

  // Extract unique filter options
  const filterOptions = useMemo(() => {
    const days = new Set();
    const courts = new Set();
    const teams = new Set();
    const divisions = new Set();
    
    eventGames.forEach(game => {
      if (game.home_team_name) teams.add(game.home_team_name);
      if (game.away_team_name) teams.add(game.away_team_name);
      if (game.scheduled_time || game.scheduled_at) {
        const date = new Date(game.scheduled_time || game.scheduled_at);
        days.add(date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }));
      }
      if (game.court_name) courts.add(game.court_name);
      if (game.location) courts.add(game.location);
      if (game.division) divisions.add(game.division);
    });
    
    return {
      days: Array.from(days),
      courts: Array.from(courts).sort(),
      teams: Array.from(teams).sort(),
      divisions: Array.from(divisions).sort()
    };
  }, [eventGames]);

  // Filter games
  const filteredGames = useMemo(() => {
    return eventGames.filter(game => {
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesSearch = 
          (game.home_team_name?.toLowerCase().includes(query)) ||
          (game.away_team_name?.toLowerCase().includes(query)) ||
          (game.title?.toLowerCase().includes(query)) ||
          (game.division?.toLowerCase().includes(query));
        if (!matchesSearch) return false;
      }
      
      if (dayFilter && (game.scheduled_time || game.scheduled_at)) {
        const gameDay = new Date(game.scheduled_time || game.scheduled_at).toLocaleDateString('en-US', { 
          weekday: 'short', month: 'short', day: 'numeric' 
        });
        if (gameDay !== dayFilter) return false;
      }
      
      if (courtFilter) {
        if (game.location !== courtFilter && game.court_name !== courtFilter) {
          return false;
        }
      }

      if (divisionFilter) {
        if (game.division !== divisionFilter) return false;
      }
      
      return true;
    });
  }, [eventGames, searchQuery, dayFilter, courtFilter, divisionFilter]);

  // Separate live and upcoming/final games
  const liveGames = filteredGames.filter(g => g.status === 'live' || g.status === 'halftime');
  const upcomingGames = filteredGames.filter(g => g.status === 'scheduled' || g.status === 'upcoming');
  const replayGames = filteredGames.filter(g => g.status === 'final' || g.status === 'completed' || g.status === 'ended');

  const formatGameTime = (dateStr) => {
    if (!dateStr) return { time: 'TBD', date: '' };
    const date = new Date(dateStr);
    return {
      time: date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }),
      date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    };
  };

  // Cinematic Live Game Card
  const LiveGameCard = ({ game }) => {
    const gameTime = formatGameTime(game.scheduled_time || game.scheduled_at);
    return (
      <Link 
        to={`/game-live/${game.game_id}`}
        data-testid={`live-game-${game.game_id}`}
        className="group relative block overflow-hidden rounded-2xl border border-[#E5FF00]/30 hover:border-[#E5FF00]/60 bg-black transition-all duration-300 shadow-lg shadow-[#E5FF00]/5"
      >
        {/* Animated live accent bar */}
        <div className="h-1 w-full bg-gradient-to-r from-[#E5FF00] via-[#FF453A] to-[#E5FF00] animate-pulse" />
        
        <div className="p-5 md:p-6">
          {/* Top row: LIVE badge + logo */}
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-2 bg-black/60 border border-[#E5FF00]/30 px-3 py-1.5 rounded-full">
              <span className="w-2.5 h-2.5 bg-[#E5FF00] rounded-full animate-pulse" />
              <span className="text-[#E5FF00] text-xs font-bold tracking-widest">
                {game.status === 'halftime' ? 'HALFTIME' : 'LIVE'}
              </span>
            </div>
            <img src={eventLogoUrl} alt="" className="w-12 h-12 md:w-14 md:h-14 object-contain opacity-60" />
          </div>
          
          {/* Teams + Scores */}
          <div className="space-y-3 mb-4">
            <div className="flex items-center justify-between">
              <span 
                className="text-xl md:text-3xl font-bold text-white uppercase tracking-tight truncate flex-1"
                style={{ fontFamily: 'Oswald, sans-serif' }}
              >
                {game.home_team_name || 'HOME'}
              </span>
              <span 
                className="text-3xl md:text-5xl font-black text-[#E5FF00] tabular-nums ml-4"
                style={{ fontFamily: 'Oswald, sans-serif' }}
              >
                {game.score?.home ?? 0}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span 
                className="text-xl md:text-3xl font-bold text-zinc-400 uppercase tracking-tight truncate flex-1"
                style={{ fontFamily: 'Oswald, sans-serif' }}
              >
                {game.away_team_name || 'AWAY'}
              </span>
              <span 
                className="text-3xl md:text-5xl font-black text-zinc-400 tabular-nums ml-4"
                style={{ fontFamily: 'Oswald, sans-serif' }}
              >
                {game.score?.away ?? 0}
              </span>
            </div>
          </div>
          
          {/* Meta + Watch button */}
          <div className="flex items-center justify-between pt-4 border-t border-white/10">
            <div className="flex items-center gap-3 text-xs text-zinc-500 uppercase tracking-widest min-w-0">
              <span className="truncate max-w-[120px]">{game.court_name || 'Main Court'}</span>
              <span className="w-1 h-1 bg-zinc-600 rounded-full shrink-0" />
              <span className="truncate">{event?.name}</span>
            </div>
            <div className="flex items-center gap-2 bg-[#FF453A] text-white px-4 py-2 rounded-full font-semibold text-xs shadow-lg shadow-[#FF453A]/30 group-hover:scale-105 transition-transform">
              <Play className="w-3.5 h-3.5" fill="currentColor" />
              Watch
            </div>
          </div>
        </div>
      </Link>
    );
  };

  // Replay Game Card (similar to live but with REPLAY badge)
  const ReplayGameCard = ({ game }) => {
    const gameTime = formatGameTime(game.scheduled_time || game.scheduled_at);
    return (
      <Link 
        to={`/game-live/${game.game_id}`}
        data-testid={`replay-game-${game.game_id}`}
        className="group relative block overflow-hidden rounded-xl border border-white/10 hover:border-[#FF453A]/40 bg-black transition-all duration-300"
      >
        {/* Accent gradient bar at top */}
        <div className="h-1 w-full bg-gradient-to-r from-[#FF453A] via-[#FF453A]/60 to-transparent" />
        
        {/* Card content */}
        <div className="p-4 sm:p-5">
          {/* Top row: badge + logo */}
          <div className="flex items-center justify-between mb-4">
            <div className="bg-white/10 backdrop-blur-md px-2.5 py-1 rounded text-[10px] font-bold text-zinc-300 uppercase tracking-wider">
              Replay
            </div>
            <img 
              src={eventLogoUrl} 
              alt=""
              className="w-10 h-10 object-contain opacity-50"
            />
          </div>
          
          {/* Teams vs layout */}
          <div className="space-y-2 mb-3">
            <div className="flex items-center justify-between">
              <span 
                className="text-base sm:text-lg font-bold text-white uppercase tracking-tight truncate flex-1"
                style={{ fontFamily: 'Oswald, sans-serif' }}
              >
                {game.home_team_name || 'HOME'}
              </span>
              <span 
                className="text-xl sm:text-2xl font-bold text-[#FF453A] tabular-nums ml-3"
                style={{ fontFamily: 'Oswald, sans-serif' }}
              >
                {game.score?.home ?? 0}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span 
                className="text-base sm:text-lg font-bold text-zinc-400 uppercase tracking-tight truncate flex-1"
                style={{ fontFamily: 'Oswald, sans-serif' }}
              >
                {game.away_team_name || 'AWAY'}
              </span>
              <span 
                className="text-xl sm:text-2xl font-bold text-zinc-400 tabular-nums ml-3"
                style={{ fontFamily: 'Oswald, sans-serif' }}
              >
                {game.score?.away ?? 0}
              </span>
            </div>
          </div>
          
          {/* Meta + play button */}
          <div className="flex items-center justify-between pt-3 border-t border-white/10">
            <p className="text-[11px] text-zinc-500 truncate max-w-[180px]">
              {gameTime.date} {game.court_name ? `\u00B7 ${game.court_name}` : ''}
            </p>
            <div className="w-8 h-8 rounded-full bg-[#FF453A]/20 flex items-center justify-center group-hover:bg-[#FF453A] transition-colors">
              <Play className="w-3.5 h-3.5 text-[#FF453A] group-hover:text-white ml-0.5" fill="currentColor" />
            </div>
          </div>
        </div>
      </Link>
    );
  };

  // Flight Board Row for Upcoming Games
  const UpcomingGameRow = ({ game, index }) => {
    const gameTime = formatGameTime(game.scheduled_time || game.scheduled_at);
    return (
      <Link 
        to={`/game-live/${game.game_id}`}
        data-testid={`upcoming-game-${game.game_id}`}
        className="group flex items-center py-4 border-b border-white/10 hover:bg-white/5 transition-all duration-300"
        style={{ animationDelay: `${index * 50}ms` }}
      >
        {/* Time */}
        <div className="w-20 md:w-28 shrink-0">
          <p className="text-sm text-zinc-400" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {gameTime.time}
          </p>
          <p className="text-[10px] text-zinc-600 uppercase tracking-wider">
            {gameTime.date}
          </p>
        </div>
        
        {/* Teams - stacked layout */}
        <div className="flex-1 min-w-0 transform transition-transform duration-300 group-hover:translate-x-1">
          <p 
            className="text-sm md:text-base font-bold text-white uppercase tracking-wide leading-tight truncate"
            style={{ fontFamily: 'Oswald, sans-serif' }}
          >
            {game.home_team_name}
          </p>
          <p 
            className="text-sm md:text-base font-bold text-zinc-400 uppercase tracking-wide leading-tight mt-0.5 truncate"
            style={{ fontFamily: 'Oswald, sans-serif' }}
          >
            <span className="text-zinc-600 text-xs font-normal mr-1">vs</span>{game.away_team_name}
          </p>
          {game.division && (
            <p className="text-[10px] text-zinc-600 uppercase tracking-widest mt-1 truncate">{game.division}</p>
          )}
        </div>
        
        {/* Court */}
        <div className="shrink-0 ml-3 max-w-[100px] md:max-w-[140px]">
          <span className="text-[10px] md:text-xs font-bold text-[#FF453A] uppercase tracking-[0.2em] truncate block">
            {game.court_name || game.location || 'TBD'}
          </span>
        </div>
      </Link>
    );
  };

  // Filter Pill Component
  const FilterPill = ({ active, onClick, children }) => (
    <button
      onClick={onClick}
      className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 whitespace-nowrap ${
        active 
          ? 'bg-white text-black' 
          : 'bg-transparent border border-white/20 text-white hover:bg-white/10'
      }`}
    >
      {children}
    </button>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#FF453A]" />
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex flex-col items-center justify-center text-white px-4">
        <div className="text-6xl mb-4">🏀</div>
        <h2 
          className="text-3xl font-bold mb-2 uppercase tracking-tight"
          style={{ fontFamily: 'Oswald, sans-serif' }}
        >
          {error ? 'Connection Error' : 'Event Not Found'}
        </h2>
        <p className="text-zinc-500 mb-6">
          {error || "The event you're looking for doesn't exist."}
        </p>
        <div className="flex items-center justify-center gap-3">
          {error && (
            <Button 
              onClick={() => fetchEvent()}
              className="bg-[#FF453A] hover:bg-[#FF5A50] text-white font-bold rounded-full px-6"
            >
              Try Again
            </Button>
          )}
          <Link to="/events">
            <Button variant={error ? "outline" : "default"} className={error ? "border-zinc-600 text-zinc-300 rounded-full px-6" : "bg-[#FF453A] hover:bg-[#FF5A50] text-white font-bold rounded-full px-6"}>
              <ChevronLeft className="h-4 w-4 mr-2" /> Back to Events
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <SeoHead
        title={event.name || 'Event'}
        description={
          event.description ||
          `${event.name || 'Event'} on Rooam Sports Network. Live streaming, real-time stats, full game replays, and athlete highlights.`
        }
        path={`/event/${event.id || id}`}
        image={eventLogoUrl}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "SportsEvent",
          "name": event.name,
          "description": event.description || `${event.name} live on Rooam Sports Network`,
          "startDate": event.start_date,
          "endDate": event.end_date || event.start_date,
          "eventStatus": "https://schema.org/EventScheduled",
          "eventAttendanceMode": "https://schema.org/MixedEventAttendanceMode",
          "location": event.location ? { "@type": "Place", "name": event.location } : undefined,
          "image": eventLogoUrl && (eventLogoUrl.startsWith('http') ? eventLogoUrl : `https://rooamsports.network${eventLogoUrl}`),
          "url": `https://rooamsports.network/event/${event.id || id}`,
          "organizer": {
            "@type": "Organization",
            "name": "Rooam Sports Network",
            "url": "https://rooamsports.network"
          }
        }}
      />
      {/* Cinematic Hero Section */}
      <div className="relative w-full overflow-hidden bg-black" style={{ minHeight: '340px' }}>
        {/* Background: Event logo centered with radial glow */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 flex items-center justify-center">
            <img 
              src={eventLogoUrl}
              alt=""
              className="w-48 h-48 sm:w-56 sm:h-56 md:w-72 md:h-72 lg:w-80 lg:h-80 object-contain opacity-15"
            />
          </div>
          {/* Subtle radial glow behind logo */}
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(255,69,58,0.08)_0%,transparent_70%)]" />
          {/* Bottom gradient for text readability */}
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-[#0A0A0A]/40" />
          {/* Accent line at bottom */}
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#FF453A] to-transparent opacity-40" />
        </div>
        
        {/* Back Button */}
        <div className="absolute top-4 left-4 z-20">
          <Link 
            to="/events" 
            className="inline-flex items-center gap-2 text-zinc-400 hover:text-white text-sm transition-colors bg-black/30 backdrop-blur-md px-3 py-1.5 rounded-full"
          >
            <ChevronLeft className="h-4 w-4" /> Back to Events
          </Link>
        </div>
        
        {/* Hero Content */}
        <div className="relative z-10 flex flex-col items-center justify-end h-full px-4 md:px-8 pb-8 pt-16" style={{ minHeight: '340px' }}>
          {/* Mobile logo (visible on small screens) */}
          <img 
            src={eventLogoUrl}
            alt=""
            className="md:hidden w-24 h-24 object-contain mb-4 drop-shadow-2xl"
          />
          
          {/* Event Title - centered */}
          <h1 
            className="text-3xl sm:text-4xl md:text-6xl lg:text-7xl font-black uppercase tracking-tighter text-white leading-none text-center max-w-4xl"
            style={{ fontFamily: 'Oswald, sans-serif' }}
            data-testid="event-title"
          >
            {event.name}
          </h1>
          
          {/* Meta Pills - centered */}
          <div className="flex flex-wrap justify-center gap-3 mt-5">
            {event.date && (
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur-md px-4 py-2 rounded-full">
                <Calendar className="h-4 w-4 text-[#FF453A]" />
                <span className="text-sm font-medium">
                  {(() => {
                    const str = String(event.date);
                    let d;
                    if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
                      const [y,m,dy] = str.split('-').map(Number);
                      d = new Date(y, m-1, dy);
                    } else {
                      const dt = new Date(str);
                      d = new Date(dt.getUTCFullYear(), dt.getUTCMonth(), dt.getUTCDate());
                    }
                    let label = d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
                    if (event.end_date && event.end_date !== event.date) {
                      const eStr = String(event.end_date);
                      let ed;
                      if (/^\d{4}-\d{2}-\d{2}$/.test(eStr)) {
                        const [y2,m2,dy2] = eStr.split('-').map(Number);
                        ed = new Date(y2, m2-1, dy2);
                      } else {
                        const dt2 = new Date(eStr);
                        ed = new Date(dt2.getUTCFullYear(), dt2.getUTCMonth(), dt2.getUTCDate());
                      }
                      label += ` - ${ed.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}`;
                    }
                    return label;
                  })()}
                </span>
              </div>
            )}
            {event.location && (
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur-md px-4 py-2 rounded-full">
                <MapPin className="h-4 w-4 text-[#FF453A]" />
                <span className="text-sm font-medium">{event.location}</span>
              </div>
            )}
            <div className="flex items-center gap-2 bg-white/10 backdrop-blur-md px-4 py-2 rounded-full">
              <span className="text-sm font-medium">{eventGames.length} Games</span>
            </div>
            {event.free_with_account && (
              <div className="flex items-center gap-2 bg-emerald-500/20 backdrop-blur-md px-4 py-2 rounded-full border border-emerald-400/40" data-testid="free-with-account-badge">
                <Check className="h-4 w-4 text-emerald-400" />
                <span className="text-sm font-bold text-emerald-300 uppercase tracking-wider" style={{ fontFamily: 'Oswald, sans-serif' }}>
                  Free with Rooam Account
                </span>
              </div>
            )}
          </div>

          {/* CTA for non-logged-in users on free-with-account events */}
          {event.free_with_account && !isAuthenticated() && (
            <div className="mt-6 flex flex-col sm:flex-row gap-3 items-center">
              <button
                onClick={() => navigate(`/register?from=event:${event.id}&return_to=/event/${event.id}`)}
                className="px-8 py-3 rounded-full bg-emerald-500 hover:bg-emerald-400 text-black font-bold uppercase tracking-wider shadow-lg shadow-emerald-500/30 transition-all hover:scale-105"
                style={{ fontFamily: 'Oswald, sans-serif' }}
                data-testid="free-signup-cta"
              >
                Create Free Account to Watch
              </button>
              <button
                onClick={() => navigate(`/login?return_to=/event/${event.id}`)}
                className="px-6 py-3 rounded-full border border-white/20 hover:border-white/40 text-white font-medium transition-all"
                data-testid="free-login-cta"
              >
                Already have an account? Log in
              </button>
            </div>
          )}
        </div>
      </div>
      
      {/* Event Passes Section */}
      {passTiers.length > 0 && (
        <div className="bg-[#111]/80 border-b border-white/10">
          <div className="max-w-7xl mx-auto px-4 md:px-8 py-6">
            <div className="flex items-center gap-3 mb-4">
              <Ticket className="w-4 h-4 text-[#FF453A]" />
              <h2 
                className="text-xs font-bold text-zinc-400 uppercase tracking-[0.2em]"
                style={{ fontFamily: 'Manrope, sans-serif' }}
              >
                Event Passes
              </h2>
              {userAccess?.has_access && userAccess.access_type === 'event_pass' && (
                <span className="ml-auto flex items-center gap-1.5 text-xs text-emerald-400 font-medium bg-emerald-400/10 px-3 py-1 rounded-full">
                  <Check className="w-3 h-3" />
                  Pass Active
                </span>
              )}
              {userAccess?.has_access && userAccess.access_type === 'free_account' && (
                <span className="ml-auto flex items-center gap-1.5 text-xs text-emerald-400 font-medium bg-emerald-400/10 px-3 py-1 rounded-full" data-testid="free-account-active-badge">
                  <Check className="w-3 h-3" />
                  Free Access Unlocked
                </span>
              )}
              {userAccess && !userAccess.has_access && userAccess.requires_pass && (
                <span className="ml-auto flex items-center gap-1.5 text-xs text-amber-400 font-medium bg-amber-400/10 px-3 py-1 rounded-full">
                  <Ticket className="w-3 h-3" />
                  Event Pass Required
                </span>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {passTiers.map(tier => {
                const owned = userAccess?.passes?.some(p => p.pass_tier_id === tier.id);
                const hasAnyAccess = userAccess?.has_access;
                return (
                  <div 
                    key={tier.id}
                    data-testid={`pass-tier-${tier.id}`}
                    className={`relative p-4 rounded-xl border transition-all duration-300 ${
                      owned 
                        ? 'border-emerald-500/40 bg-emerald-500/5' 
                        : 'border-white/10 bg-[#141414] hover:border-[#FF453A]/30'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <h3 
                          className="text-base font-bold text-white uppercase tracking-tight"
                          style={{ fontFamily: 'Oswald, sans-serif' }}
                        >
                          {tier.name}
                        </h3>
                        {tier.description && (
                          <p className="text-xs text-zinc-500 mt-1 line-clamp-2">{tier.description}</p>
                        )}
                      </div>
                      <p 
                        className="text-2xl font-bold text-[#FF453A] tabular-nums shrink-0"
                        style={{ fontFamily: 'Oswald, sans-serif' }}
                      >
                        ${tier.price?.toFixed(0)}
                      </p>
                    </div>
                    <div className="mt-3">
                      {owned ? (
                        <div className="flex items-center gap-2 text-sm text-emerald-400 font-medium">
                          <Check className="w-4 h-4" /> Purchased
                        </div>
                      ) : (
                        <>
                          <Button
                            size="sm"
                            disabled={checkoutLoading === tier.id}
                            onClick={() => handleBuyPass(tier.id)}
                            data-testid={`buy-pass-${tier.id}`}
                            className="w-full bg-[#FF453A] hover:bg-[#FF5A50] text-white font-bold text-sm rounded-full"
                          >
                            {checkoutLoading === tier.id ? (
                              <Loader2 className="w-4 h-4 animate-spin mr-2" />
                            ) : (
                              <Ticket className="w-4 h-4 mr-2" />
                            )}
                            {checkoutLoading === tier.id ? 'Loading...' : 'Buy Pass'}
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="mt-4 p-3 bg-zinc-900/60 border border-white/5 rounded-lg" data-testid="event-pass-disclaimer">
              <p className="text-[11px] text-zinc-500 leading-relaxed">
                By purchasing an event pass you agree to the following: All event pass sales are <span className="text-zinc-400 font-medium">final and non-refundable</span> unless the event is canceled or a paid stream fails to function. Event passes grant access only to games within this specific event and are separate from memberships. Refund requests for canceled events or non-functional streams can be submitted to <a href="mailto:support@rooamsports.network" className="text-cyan-500 hover:underline">support@rooamsports.network</a>.
              </p>
            </div>
            {!isAuthenticated() && (
              <p className="text-xs text-zinc-600 mt-3 text-center">
                <Lock className="w-3 h-3 inline mr-1" />
                Create an account or log in to purchase an event pass.
                <span className="text-zinc-400 ml-1">Event passes are separate from memberships.</span>
              </p>
            )}
          </div>
        </div>
      )}

      {/* Sticky Command Bar */}
      <div className="sticky top-0 z-40 bg-[#0A0A0A]/80 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-4">
          <div className="flex items-center justify-between gap-4">
            {/* Tabs */}
            <div className="flex items-center gap-6 md:gap-8">
              {['games', 'teams', 'overview', 'schedule'].map(tab => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  data-testid={`tab-${tab}`}
                  className={`text-lg md:text-2xl font-bold uppercase tracking-tight transition-colors ${
                    activeTab === tab ? 'text-white' : 'text-zinc-600 hover:text-zinc-400'
                  }`}
                  style={{ fontFamily: 'Oswald, sans-serif' }}
                >
                  {tab}
                </button>
              ))}
            </div>
            
            {/* Search Toggle */}
            <button 
              onClick={() => setShowSearch(!showSearch)}
              className="p-2 rounded-full hover:bg-white/10 transition-colors"
              data-testid="search-toggle"
            >
              {showSearch ? <X className="w-5 h-5" /> : <Search className="w-5 h-5" />}
            </button>
          </div>
          
          {/* Expandable Search */}
          {showSearch && (
            <div className="mt-4 pb-2">
              <input
                type="text"
                placeholder="Search teams..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                data-testid="search-input"
                className="w-full bg-white/5 border border-white/10 rounded-full px-5 py-3 text-white placeholder:text-zinc-600 focus:outline-none focus:border-[#FF453A]/50"
                autoFocus
              />
            </div>
          )}
          
          {/* Horizontal Filter Pills */}
          {activeTab === 'games' && (filterOptions.days.length > 0 || filterOptions.courts.length > 0) && (
            <div className="flex items-center gap-3 mt-4 overflow-x-auto pb-2 scrollbar-hide">
              <FilterPill active={!dayFilter && !courtFilter} onClick={() => { setDayFilter(''); setCourtFilter(''); setDivisionFilter(''); }}>
                All
              </FilterPill>
              
              {filterOptions.days.map(day => (
                <FilterPill 
                  key={day}
                  active={dayFilter === day}
                  onClick={() => setDayFilter(dayFilter === day ? '' : day)}
                >
                  {day}
                </FilterPill>
              ))}
              
              {filterOptions.courts.map(court => (
                <FilterPill 
                  key={court}
                  active={courtFilter === court}
                  onClick={() => setCourtFilter(courtFilter === court ? '' : court)}
                >
                  {court}
                </FilterPill>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        {activeTab === 'games' && (
          <div className="space-y-12">
            {/* Live Games - Cinematic Cards */}
            {liveGames.length > 0 && (
              <section>
                <div className="flex items-center gap-3 mb-6">
                  <span className="w-2 h-2 bg-[#E5FF00] rounded-full animate-pulse" />
                  <h2 
                    className="text-xs font-bold text-zinc-400 uppercase tracking-[0.2em]"
                    style={{ fontFamily: 'Manrope, sans-serif' }}
                  >
                    Live Now
                  </h2>
                </div>
                <div className="grid grid-cols-1 gap-6">
                  {liveGames.map(game => (
                    <LiveGameCard key={game.game_id} game={game} />
                  ))}
                </div>
              </section>
            )}
            
            {/* Replay Games */}
            {replayGames.length > 0 && (
              <section>
                <h2 
                  className="text-xs font-bold text-zinc-400 uppercase tracking-[0.2em] mb-6"
                  style={{ fontFamily: 'Manrope, sans-serif' }}
                >
                  Replays
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {replayGames.map(game => (
                    <ReplayGameCard key={game.game_id} game={game} />
                  ))}
                </div>
              </section>
            )}
            
            {/* Upcoming Games - Flight Board Style */}
            {upcomingGames.length > 0 && (
              <section>
                <h2 
                  className="text-xs font-bold text-zinc-400 uppercase tracking-[0.2em] mb-6"
                  style={{ fontFamily: 'Manrope, sans-serif' }}
                >
                  Upcoming
                </h2>
                <div className="bg-[#141414] rounded-2xl border border-white/10 overflow-hidden">
                  <div className="divide-y divide-white/5 px-4 md:px-6">
                    {upcomingGames.map((game, idx) => (
                      <UpcomingGameRow key={game.game_id} game={game} index={idx} />
                    ))}
                  </div>
                </div>
              </section>
            )}
            
            {/* Empty State */}
            {filteredGames.length === 0 && (
              <div className="text-center py-20">
                <p className="text-zinc-500 text-lg">No games found</p>
                <button 
                  onClick={() => { setDayFilter(''); setCourtFilter(''); setSearchQuery(''); }}
                  className="mt-4 text-[#FF453A] hover:text-[#FF5A50] font-medium"
                >
                  Clear filters
                </button>
              </div>
            )}
          </div>
        )}

        {activeTab === 'teams' && (
          <div>
            <h2 
              className="text-xs font-bold text-zinc-400 uppercase tracking-[0.2em] mb-6"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              {filterOptions.teams.length} Teams
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {filterOptions.teams.map(team => {
                const teamGames = eventGames.filter(g => g.home_team_name === team || g.away_team_name === team);
                const teamColor = teamGames.length > 0 
                  ? (teamGames[0].home_team_name === team ? teamGames[0].home_team_color : teamGames[0].away_team_color) || '#FF453A'
                  : '#FF453A';
                return (
                  <Link 
                    key={team}
                    to={`/team/${encodeURIComponent(team)}`}
                    className="p-4 bg-[#141414] border border-white/10 rounded-xl hover:border-[#FF453A]/50 hover:bg-[#1C1C1E] transition-all duration-300 group flex items-center gap-3"
                    data-testid={`team-${team}`}
                  >
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm shrink-0"
                      style={{ backgroundColor: teamColor }}
                    >
                      {team?.charAt(0)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p 
                        className="font-bold text-white uppercase tracking-tight truncate group-hover:text-[#FF453A] transition-colors text-sm"
                        style={{ fontFamily: 'Oswald, sans-serif' }}
                      >
                        {team}
                      </p>
                      <p className="text-xs text-zinc-500 mt-0.5">
                        {teamGames.length} game{teamGames.length !== 1 ? 's' : ''} {event.sport ? `\u00B7 ${event.sport}` : ''}
                      </p>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        )}

        {activeTab === 'overview' && (
          <div className="grid md:grid-cols-2 gap-4">
            {/* Event Details */}
            <div className="bg-[#141414] rounded-2xl border border-white/10 p-6">
              <h3 className="text-xs font-bold text-zinc-400 uppercase tracking-[0.2em] mb-5" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Event Details
              </h3>
              <div className="space-y-4">
                <div className="flex justify-between">
                  <span className="text-zinc-500 text-sm">Sport</span>
                  <span className="text-white text-sm font-medium capitalize">{event.sport || 'Basketball'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-zinc-500 text-sm">Teams</span>
                  <span className="text-white text-sm font-medium">{filterOptions.teams.length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-zinc-500 text-sm">Games</span>
                  <span className="text-white text-sm font-medium">{eventGames.length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-zinc-500 text-sm">Status</span>
                  <span className={`text-sm font-medium capitalize ${
                    event.status === 'live' ? 'text-[#E5FF00]' : event.status === 'completed' ? 'text-zinc-400' : 'text-emerald-400'
                  }`}>{event.status || 'Upcoming'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-zinc-500 text-sm">Location</span>
                  <span className="text-white text-sm font-medium">{event.location}</span>
                </div>
                {event.description && (
                  <div className="pt-2 border-t border-white/5">
                    <p className="text-zinc-400 text-sm leading-relaxed">{event.description}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Courts & Venues */}
            <div className="bg-[#141414] rounded-2xl border border-white/10 p-6">
              <h3 className="text-xs font-bold text-zinc-400 uppercase tracking-[0.2em] mb-5" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Courts
              </h3>
              {filterOptions.courts.length > 0 ? (
                <div className="space-y-3">
                  {filterOptions.courts.map(court => {
                    const courtGames = eventGames.filter(g => g.court_name === court);
                    return (
                      <div key={court} className="flex items-center justify-between p-3 bg-black/40 rounded-lg">
                        <span className="text-white text-sm font-medium">{court}</span>
                        <span className="text-zinc-500 text-xs">{courtGames.length} games</span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-zinc-500 text-sm">No court assignments yet</p>
              )}
            </div>
          </div>
        )}

        {activeTab === 'schedule' && (
          <div className="bg-[#141414] rounded-2xl border border-white/10 overflow-hidden">
            <div className="divide-y divide-white/5 px-4 md:px-6">
              {eventGames.map((game, idx) => {
                const gameTime = formatGameTime(game.scheduled_time || game.scheduled_at);
                const isLive = game.status === 'live' || game.status === 'halftime';
                const isFinal = game.status === 'final' || game.status === 'completed';
                
                return (
                  <Link 
                    key={game.game_id}
                    to={`/game-live/${game.game_id}`}
                    data-testid={`schedule-${game.game_id}`}
                    className="group flex items-center py-5 hover:bg-white/5 transition-all duration-300"
                  >
                    {/* Time */}
                    <div className="w-24 md:w-32 shrink-0">
                      {isLive ? (
                        <div className="flex items-center gap-2">
                          <span className="w-2 h-2 bg-[#E5FF00] rounded-full animate-pulse" />
                          <span className="text-xs font-bold text-[#E5FF00] uppercase">
                            {game.status === 'halftime' ? 'Half' : 'Live'}
                          </span>
                        </div>
                      ) : (
                        <>
                          <p className="text-sm text-zinc-400">{gameTime.time}</p>
                          <p className="text-[10px] text-zinc-600 uppercase">{gameTime.date}</p>
                        </>
                      )}
                    </div>
                    
                    {/* Teams */}
                    <div className="flex-1 min-w-0 transform transition-transform duration-300 group-hover:translate-x-1">
                      <p 
                        className="text-sm md:text-base font-bold text-white uppercase tracking-wide leading-tight truncate"
                        style={{ fontFamily: 'Oswald, sans-serif' }}
                      >
                        {game.home_team_name}
                      </p>
                      <p 
                        className="text-sm md:text-base font-bold text-zinc-400 uppercase tracking-wide leading-tight mt-0.5 truncate"
                        style={{ fontFamily: 'Oswald, sans-serif' }}
                      >
                        <span className="text-zinc-600 text-xs font-normal mr-1">vs</span>{game.away_team_name}
                      </p>
                      <p className="text-[10px] text-zinc-600 uppercase tracking-wider mt-1 truncate">
                        {game.division ? `${game.division} • ` : ''}{game.court_name || game.location}
                      </p>
                    </div>
                    
                    {/* Score or Status */}
                    <div className="shrink-0 ml-4 text-right">
                      {isFinal ? (
                        <p 
                          className="text-xl font-bold text-white tabular-nums"
                          style={{ fontFamily: 'Oswald, sans-serif' }}
                        >
                          {game.score?.home ?? 0} - {game.score?.away ?? 0}
                        </p>
                      ) : isLive ? (
                        <p 
                          className="text-xl font-bold text-[#FF453A] tabular-nums"
                          style={{ fontFamily: 'Oswald, sans-serif' }}
                        >
                          {game.score?.home ?? 0} - {game.score?.away ?? 0}
                        </p>
                      ) : (
                        <span className="text-xs text-zinc-500 uppercase">Upcoming</span>
                      )}
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EventDetail;
